import {
	Disclosure,
	DisclosureButton,
	DisclosurePanel,
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	MenuSeparator,
	Transition,
} from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { Link, Outlet } from 'react-router-dom';

import Logo from 'jsx:./img/tandem-logo-horizontal.svg';
import NotificationsContainer from './components/NotificationsContainer';
import { useUserStore } from './stores/userStore';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

const Layout = () => {
	const { isSignedIn } = useUserStore();
	return (
		// Full-height flex column so the footer stays at the viewport bottom even
		// when the page content is shorter than the screen.
		<div className="flex flex-col min-h-screen">
			<Disclosure as="nav">
				{({ open }) => (
					<>
						<div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
							<div className="flex justify-between h-16">
								<div className="flex">
									<div className="flex items-center flex-shrink-0">
										<Link className="text-4xl logo btn btn-ghost" to="/">
											<Logo className="w-auto h-10" />
										</Link>
									</div>
								</div>
								<div className="hidden sm:ml-6 sm:flex sm:items-center">
									<Menu as="div" className="relative ml-3">
										<div className="mr-4">
											<MenuButton className="relative flex p-2 text-sm bg-white rounded-md btn btn-ghost focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
												<span className="absolute -inset-1.5" />
												<span className="sr-only">Open user menu</span>
												<Bars3Icon
													className="block w-6 h-6"
													aria-hidden="true"
												/>
											</MenuButton>
										</div>
										<Transition
											as={Fragment}
											enter="transition ease-out duration-200"
											enterFrom="transform opacity-0 scale-95"
											enterTo="transform opacity-100 scale-100"
											leave="transition ease-in duration-75"
											leaveFrom="transform opacity-100 scale-100"
											leaveTo="transform opacity-0 scale-95"
										>
											<MenuItems className="absolute right-0 z-10 w-48 py-1 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
												{isSignedIn() ? (
													<>
														<MenuItem>
															{({ active }) => (
																<Link
																	to="/home"
																	className={classNames(
																		active ? 'bg-gray-100' : '',
																		'block px-4 py-2 text-sm text-gray-700',
																	)}
																>
																	Home
																</Link>
															)}
														</MenuItem>
														<MenuItem>
															{({ active }) => (
																<Link
																	to="/profile"
																	className={classNames(
																		active ? 'bg-gray-100' : '',
																		'block px-4 py-2 text-sm text-gray-700',
																	)}
																>
																	Your Profile
																</Link>
															)}
														</MenuItem>
														<MenuItem>
															{({ active }) => (
																<Link
																	to="/personalFinances"
																	className={classNames(
																		active ? 'bg-gray-100' : '',
																		'block px-4 py-2 text-sm text-gray-700',
																	)}
																>
																	Personal Finances
																</Link>
															)}
														</MenuItem>
														<MenuItem>
															{({ active }) => (
																<Link
																	to="/assessments"
																	className={classNames(
																		active ? 'bg-gray-100' : '',
																		'block px-4 py-2 text-sm text-gray-700',
																	)}
																>
																	Questionnaires
																</Link>
															)}
														</MenuItem>
														<MenuSeparator />
														<MenuItem>
															{({ active }) => (
																<Link
																	to="/logout"
																	className={classNames(
																		active ? 'bg-gray-100' : '',
																		'block px-4 py-2 text-sm text-gray-700',
																	)}
																>
																	Sign out
																</Link>
															)}
														</MenuItem>
													</>
												) : (
													<MenuItem>
														{({ active }) => (
															<Link
																to="/login"
																className={classNames(
																	active ? 'bg-gray-100' : '',
																	'block px-4 py-2 text-sm text-gray-700',
																)}
															>
																Sign in
															</Link>
														)}
													</MenuItem>
												)}
											</MenuItems>
										</Transition>
									</Menu>
								</div>
								<div className="flex items-center -mr-2 sm:hidden">
									<DisclosureButton className="relative inline-flex items-center justify-center p-2 text-gray-400 rounded-md hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
										<span className="absolute -inset-0.5" />
										<span className="sr-only">Open main menu</span>
										{open ? (
											<XMarkIcon className="block w-6 h-6" aria-hidden="true" />
										) : (
											<Bars3Icon className="block w-6 h-6" aria-hidden="true" />
										)}
									</DisclosureButton>
								</div>
							</div>
						</div>
						<DisclosurePanel className="sm:hidden">
							{({ close }) => (
								<div className="pt-4 pb-3 border-t border-gray-200">
									<div className="mt-3 space-y-1">
										{isSignedIn() ? (
											<>
												<Link
													to="/profile"
													className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
													onClick={close}
												>
													Your Profile
												</Link>
												<Link
													to="/logout"
													className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
												>
													Sign out
												</Link>
											</>
										) : (
											<Link
												to="/login"
												className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
												onClick={close}
											>
												Sign in
											</Link>
										)}
									</div>
								</div>
							)}
						</DisclosurePanel>
					</>
				)}
			</Disclosure>
			{/* flex-1 lets the content grow to fill the space above the footer. */}
			<div className="flex-1 outlet">
				<Outlet />
			</div>
			<footer className="p-4 footer footer-center">
				<aside>
					<p>
						&copy; {new Date().getFullYear()} Tandem, Build{' '}
						{process.env.BITBUCKET_BUILD_NUMBER || 'LOCAL'}
					</p>
				</aside>
			</footer>
			<NotificationsContainer />
		</div>
	);
};

export default Layout;
